import Image from '@tiptap/extension-image';
import { ReactNodeViewRenderer } from '@tiptap/react';

import { FigureImageView } from './FigureImageView';

/**
 * `image` node, rendered through a React NodeView instead of `renderHTML`'s
 * plain `<img>` output, so a figure awaiting its signed media-access URL can
 * show a loading state instead of an `<img>` the browser would fetch early.
 *
 * The href travels as `data-href`, never `src`. `src` is suppressed at the
 * schema level (`renderHTML: () => ({})`) because ProseMirror's schema-level
 * DOM representation gets used outside the node view's control too — e.g.
 * React StrictMode's double-mount teardown briefly reconstructs the node's
 * default DOM before the node view remounts. If that fallback DOM carried a
 * real `src="s3://..."`, the browser attempts the (harmless but noisy)
 * request regardless of what the node view itself renders.
 */
export const FigureImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      src: {
        default: null,
        parseHTML: () => null,
        renderHTML: () => ({}),
      },
      'data-href': {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('data-href'),
        renderHTML: (attributes: Record<string, unknown>) => {
          const value = attributes['data-href'];

          return value ? { 'data-href': value } : {};
        },
      },
    };
  },

  parseHTML() {
    return [{ tag: 'img[data-href]' }];
  },

  addNodeView() {
    return ReactNodeViewRenderer(FigureImageView);
  },
});
